import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import * as XLSX from 'xlsx';

import { alertService } from '../../../../shared/services/alertService';
import {
  useBreadcrumb,
  BreadcrumbItem,
} from '../../../../layouts/BreadcrumbContext';
import {
  useLazyEditCounsellingQuery,
  useGetStaffQuery,
  useLazyGetSubjectQuery,
  useLazyGetTeacherListQuery,
  useBulkUploadSeniorityMutation,
} from '../../counselling.services';
import { Option } from '../../../../shared/services/commonService';

/**
 * Migrated from Angular `SeniorityExcelUploadComponent`
 * (src/app/views/pages/counselling/seniority-excel-upload).
 *
 * Lets the user download a sample template, upload a seniority-ordered .xlsx,
 * validate each row, bulk-write the seniority numbers, and export the current
 * teacher grid. Reached from the Seniority Upload Type hub, which passes the
 * counselling id + filters via router navigation state.
 *
 * This port also applies the fixes from the hardening task list:
 *  - batch uploads are aggregated (one success toast + one reload; failures
 *    are reported and stop the run)                                  [Task 1]
 *  - TeacherId length is a strict numeric === 8 check                [Task 2]
 *  - `noDataFound` reflects real load state                          [Task 3]
 *  - the header row is handled explicitly and safely                 [Task 4]
 *  - file parsing is wrapped in try/catch + reader.onerror           [Task 5]
 *  - required localStorage/navState filters are validated            [Task 6]
 *  - a missing counselling id short-circuits the API calls           [Task 7]
 *  - typed records/responses instead of `any`                        [Task 8]
 *  - the CounsellingType '4' literal is a named constant             [Task 9]
 *  - dead code dropped                                               [Task 10]
 *  - clean table markup + dynamic empty colspan                      [Task 11]
 */

// ── Task 9: named constant for the "New Appointment" counselling type ────────
const NEW_APPOINTMENT = '4';

// ── Task 8: types ────────────────────────────────────────────────────────────
interface CounsellingDetail {
  Title?: string;
  Location?: string | number;
  CounsellingType?: string;
  [key: string]: unknown;
}

interface TeacherRecord {
  seniority_state?: string | number;
  teacher_id?: string;
  application_id?: string;
  name?: string;
  subject?: string;
  desgnation?: string;
  udise_code?: string;
  school_name?: string;
  priority?: string;
  dob?: string;
  DistrictName?: string;
  HmAprvSts?: string;
  // labelized / formatted fields added client-side
  subjectLabel?: string;
  desgnationLabel?: string;
  [key: string]: unknown;
}

interface ApiResponse<T> {
  dataStatus?: boolean;
  result?: T[];
  message?: string;
}

interface ColumnDef {
  field: string;
  header: string;
}

/** Filters handed off from the hub via router navigation state. */
interface ExcelUploadNavState {
  path?: unknown;
  subject?: unknown;
  panelType?: unknown;
  block?: unknown;
  manageId?: unknown;
}

const str = (v: unknown): string =>
  v === null || v === undefined ? '' : String(v);

/** Angular used moment(dob).format('DD-MM-yyyy'); keep the same output. */
const formatDob = (value: unknown): string => {
  if (!value) return '';
  const d = new Date(value as string);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${d.getFullYear()}`;
};

// Angular teacherListSelectClms — the default (non-type-4) column set.
const DEFAULT_COLUMNS: ColumnDef[] = [
  { field: 'seniority_state', header: 'Seniority No.' },
  { field: 'teacher_id', header: 'Teacher Id' },
  { field: 'name', header: 'Name' },
  { field: 'desgnationLabel', header: 'Designation' },
  { field: 'subjectLabel', header: 'Subject' },
  { field: 'udise_code', header: 'Udise Code' },
  { field: 'school_name', header: 'School Name' },
  { field: 'priority', header: 'Priority' },
];

// Angular type-4 (New Appointment / ApplicationId) column set.
const NEW_APPOINTMENT_COLUMNS: ColumnDef[] = [
  { field: 'seniority_state', header: 'Seniority No.' },
  { field: 'application_id', header: 'Teacher Id' },
  { field: 'name', header: 'Name' },
  { field: 'desgnationLabel', header: 'Designation' },
  { field: 'DistrictName', header: 'District' },
  { field: 'dob', header: 'DOB' },
];

const downloadXlsx = (rows: object[], sheetName: string, fileBase: string) => {
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = {
    Sheets: { [sheetName]: worksheet },
    SheetNames: [sheetName],
  };
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileBase}_export_${new Date().getTime()}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
};

const SeniorityExcelUpload = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSubItems, clearSubItems } = useBreadcrumb();

  // Hand-off from the hub comes via router navigation state (clean URL).
  const navState = (location.state ?? {}) as ExcelUploadNavState;
  const counId = useMemo(() => str(navState.path), [navState.path]);

  // Filters (Angular read these from localStorage: PanelType/subject/block/ManageId).
  const localSubject = useMemo(() => str(navState.subject), [navState.subject]);
  const localPanel = useMemo(
    () => str(navState.panelType),
    [navState.panelType]
  );
  const localBlock = useMemo(() => str(navState.block), [navState.block]);
  const localMange = useMemo(() => str(navState.manageId), [navState.manageId]);

  const { data: staffRaw } = useGetStaffQuery();
  const [triggerEditCounselling] = useLazyEditCounsellingQuery();
  const [triggerGetSubject] = useLazyGetSubjectQuery();
  const [triggerGetTeacherList] = useLazyGetTeacherListQuery();
  const [bulkUploadSeniority] = useBulkUploadSeniorityMutation();

  const [counsellingDetails, setCounsellingDetails] =
    useState<CounsellingDetail | null>(null);
  const [teacherList, setTeacherList] = useState<TeacherRecord[]>([]);
  const [subList, setSubList] = useState<Option[]>([]);
  const [loading, setLoading] = useState(true);
  // Task 3: real load state. false until a load resolves; controls whether the
  // "No Records Found" message should show.
  const [noDataFound, setNoDataFound] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  // Parsed rows from the uploaded workbook (row 0 = header).
  const [uploadData, setUploadData] = useState<unknown[][]>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isNewAppointment =
    str(counsellingDetails?.CounsellingType) === NEW_APPOINTMENT;

  // Column set switches for type-4 (Angular did this in GetOneCounselling).
  const allColumns = useMemo(
    () => (isNewAppointment ? NEW_APPOINTMENT_COLUMNS : DEFAULT_COLUMNS),
    [isNewAppointment]
  );
  const [selectedCols, setSelectedCols] =
    useState<ColumnDef[]>(DEFAULT_COLUMNS);
  useEffect(() => {
    setSelectedCols(allColumns);
  }, [allColumns]);
  const orderedCols = useMemo(
    () => allColumns.filter((col) => selectedCols.includes(col)),
    [allColumns, selectedCols]
  );
  const globalFilterFields = useMemo(
    () => allColumns.map((c) => c.field),
    [allColumns]
  );

  const designationList: Option[] = useMemo(
    () => staffRaw?.AllStaffType ?? [],
    [staffRaw]
  );

  // ── Load teacher list (Angular GetAllTeacherList) ───────────────────────────
  const loadTeacherList = useCallback(
    async (
      details: CounsellingDetail,
      subjects?: Option[],
      designations?: Option[]
    ) => {
      // Use lists passed in by the caller when available. State updates are
      // async and this callback closes over whatever `subList`/`designationList`
      // existed when it was created, so on the initial load those closures can
      // still be empty (stale closure). The caller passes the freshly resolved
      // lists; reloads fall back to the current state.
      const subs = subjects ?? subList;
      const dess = designations ?? designationList;

      // Angular sent these filters as-is (empty allowed), so the call is not
      // gated on them being present.
      setLoading(true);
      try {
        const res = (await triggerGetTeacherList({
          counsellingId: counId,
          panelType: localPanel,
          subject: localSubject,
          location: details?.Location,
          blockId: localBlock,
          managementId: localMange,
        }).unwrap()) as ApiResponse<TeacherRecord>;

        if (res?.dataStatus && (res.result ?? []).length > 0) {
          const approved = (res.result ?? [])
            .filter((ftr) => ftr.HmAprvSts === 'Approved')
            .map((row) => ({
              ...row,
              seniority_state: Number(row.seniority_state),
            }))
            .sort(
              (a, b) => Number(a.seniority_state) - Number(b.seniority_state)
            )
            .map((row) => {
              const sub = subs.find((s) => str(s.value) === str(row.subject));
              const des = dess.find(
                (d) => str(d.value) === str(row.desgnation)
              );
              return {
                ...row,
                subjectLabel: sub?.label ?? '',
                desgnationLabel: des?.label ?? '',
                dob: formatDob(row.dob),
              };
            });
          setTeacherList(approved);
          setNoDataFound(approved.length === 0);
        } else {
          setTeacherList([]);
          setNoDataFound(true);
        }
      } catch {
        setTeacherList([]);
        setNoDataFound(true);
        alertService.error('Something went wrong');
      } finally {
        setLoading(false);
      }
    },
    [
      counId,
      localPanel,
      localSubject,
      localBlock,
      localMange,
      subList,
      designationList,
      triggerGetTeacherList,
    ]
  );

  // ── Load counselling + subject list, then teacher list ──────────────────────
  const loadCounselling = useCallback(async () => {
    // Task 7: guard against a missing counselling id.
    if (!counId) {
      setLoading(false);
      setNoDataFound(true);
      alertService.error(
        'No counselling selected. Please open this page from the seniority screen.'
      );
      return;
    }

    try {
      // Angular SubjectJson() -> AllSubject.
      let subjects: Option[] = [];
      try {
        const subjectData = (await triggerGetSubject().unwrap()) as Record<
          string,
          Option[]
        >;
        const list = subjectData?.AllSubject;
        subjects = Array.isArray(list)
          ? list.map((x) => ({ label: x.label, value: str(x.value) }))
          : [];
      } catch {
        subjects = [];
      }
      setSubList(subjects);

      const res = (await triggerEditCounselling({
        counsellingId: counId,
      }).unwrap()) as ApiResponse<CounsellingDetail>;

      if (res?.dataStatus && (res.result ?? []).length > 0) {
        const details = (res.result ?? [])[0];
        setCounsellingDetails(details);
        await loadTeacherList(details, subjects, designationList);
      } else {
        setLoading(false);
        setNoDataFound(true);
        alertService.error(res?.message ?? 'Something went wrong');
      }
    } catch {
      setLoading(false);
      setNoDataFound(true);
      alertService.error('Something went wrong');
    }
  }, [
    counId,
    triggerGetSubject,
    triggerEditCounselling,
    designationList,
    loadTeacherList,
  ]);

  // Wait for the staff lookup (labels depend on it) before loading.
  useEffect(() => {
    if (!staffRaw) return;
    loadCounselling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffRaw, counId]);

  // ── Sample template download (Angular excelTemplate) ────────────────────────
  const excelTemplate = useCallback(() => {
    const rows = isNewAppointment
      ? [{ Seniority: '', ApplicationId: '' }]
      : [
          {
            Seniority: '',
            TeacherId: '',
            TeacherName: '',
            Designation: '',
            Subject: '',
            UdiseCode: '',
            SchoolName: '',
            Priority: '',
          },
        ];
    downloadXlsx(rows, 'Sample Template', 'Sample Template');
  }, [isNewAppointment]);

  // ── File parse (Angular handleUploadFile) — Task 5: guarded ─────────────────
  const handleUploadFile = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const files = evt.target.files;
      if (!files || files.length !== 1) {
        alertService.error('Please select a single .xlsx file');
        return;
      }
      const file = files[0];
      if (!/\.xlsx$/i.test(file.name)) {
        alertService.error('Invalid file type. Please upload a .xlsx file');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const bstr = e.target?.result as string;
          const wb = XLSX.read(bstr, { type: 'binary' });
          const wsname = wb.SheetNames[0];
          if (!wsname) {
            alertService.error('The file has no sheets');
            setUploadData([]);
            return;
          }
          const ws = wb.Sheets[wsname];
          const rows = XLSX.utils.sheet_to_json(ws, {
            header: 1,
          }) as unknown[][];
          setUploadData(rows);
        } catch {
          setUploadData([]);
          alertService.error(
            'Could not read the file. Please upload a valid .xlsx file'
          );
        }
      };
      reader.onerror = () => {
        setUploadData([]);
        alertService.error('Failed to read the file. Please try again');
      };
      reader.readAsBinaryString(file);
    },
    []
  );

  // ── Upload (Angular handleUpload) — Tasks 1, 2, 4 ───────────────────────────
  const handleUpload = useCallback(async () => {
    // Task 4: explicit + safe header-row handling. Row 0 is the header, so we
    // need at least one header + one data row.
    if (!uploadData || uploadData.length === 0) {
      alertService.error('File is empty. Please select correct file');
      return;
    }
    if (uploadData.length < 2) {
      alertService.error(
        'No data rows found. The first row must be a header, followed by data rows.'
      );
      return;
    }

    // Map each row to an object keyed by the header cells (Angular did this).
    const headerKeys = (uploadData[0] as unknown[]).map((k) => str(k));
    const rowObjects = uploadData.map((row) => {
      const obj: Record<string, unknown> = {};
      headerKeys.forEach((key, idx) => {
        obj[key] = (row as unknown[])[idx];
      });
      return obj;
    });

    const idKey = isNewAppointment ? 'ApplicationId' : 'TeacherId';

    const apiArr: Array<{
      CnsId: string;
      TchrId: string;
      Seniority: unknown;
      counselling_type: string;
    }> = [];
    let invalidRow: Record<string, unknown> | null = null;

    // Start at index 1: skip the header row (Task 4).
    for (let i = 1; i < rowObjects.length; i++) {
      const row = rowObjects[i];
      const rawId = row[idKey];
      // Blank id rows are skipped (Angular only pushed when id was truthy).
      if (rawId === undefined || rawId === null || str(rawId) === '') continue;

      const id = str(rawId);
      const hasSeniority =
        row.Seniority !== undefined &&
        row.Seniority !== null &&
        str(row.Seniority) !== '';

      // Task 2: strict numeric length === 8 for non-type-4; type-4 only needs
      // id + Seniority present.
      const valid = isNewAppointment
        ? hasSeniority
        : id.length === 8 && hasSeniority;

      if (!valid) {
        invalidRow = row;
        break;
      }

      apiArr.push({
        CnsId: counId,
        TchrId: id,
        Seniority: row.Seniority,
        counselling_type: str(counsellingDetails?.CounsellingType),
      });
    }

    if (invalidRow) {
      alertService.error(
        `${str(invalidRow[idKey])} invalid ${idKey} (or) invalid seniority number`
      );
      return;
    }

    if (apiArr.length === 0) {
      alertService.error('No valid rows to upload');
      return;
    }

    // Task 1: chunk into batches of 1000, run them together, aggregate the
    // result, and refresh ONCE. Any failed batch stops and is reported.
    const splitSize = 1000;
    const chunks: (typeof apiArr)[] = [];
    for (let i = 0; i < apiArr.length; i += splitSize) {
      chunks.push(apiArr.slice(i, i + splitSize));
    }

    setLoading(true);
    try {
      const results = await Promise.all(
        chunks.map(
          (chunk) =>
            bulkUploadSeniority({ records: chunk }).unwrap() as Promise<
              ApiResponse<unknown>
            >
        )
      );

      const failed = results.filter((r) => !r?.dataStatus);
      if (failed.length === 0) {
        alertService.success('Updated Successfully');
      } else {
        const msg =
          failed.find((r) => r?.message)?.message ??
          `${failed.length} of ${chunks.length} batches failed`;
        alertService.error(msg);
      }
    } catch {
      alertService.error('Something went wrong while uploading');
    }

    // Always resync the grid so the UI reflects whatever actually persisted,
    // even after a partial failure (single reload, Task 1).
    if (counsellingDetails) {
      await loadTeacherList(counsellingDetails);
    } else {
      setLoading(false);
    }
  }, [
    uploadData,
    isNewAppointment,
    counId,
    counsellingDetails,
    bulkUploadSeniority,
    loadTeacherList,
  ]);

  // ── Grid export (Angular gridExcelExport) ───────────────────────────────────
  const gridExcelExport = useCallback(() => {
    setExcelLoading(true);
    const rows = teacherList.map((t) =>
      isNewAppointment
        ? {
            Seniority: t.seniority_state,
            ApplicationId: t.application_id,
            TeacherName: t.name,
            Designation: t.desgnationLabel,
            Subject: t.subjectLabel,
            DOB: t.dob,
            District: t.DistrictName,
          }
        : {
            Seniority: t.seniority_state,
            TeacherId: t.teacher_id,
            TeacherName: t.name,
            Designation: t.desgnationLabel,
            Subject: t.subjectLabel,
            UdiseCode: t.udise_code,
            SchoolName: t.school_name,
            Priority: t.priority,
          }
    );
    downloadXlsx(rows, 'Seniority_Order', 'Seniority_Order');
    setExcelLoading(false);
  }, [teacherList, isNewAppointment]);

  // ── Navigation ──────────────────────────────────────────────────────────────
  const back = useCallback(() => navigate(-1), [navigate]);
  const handleCounsellingURL = useCallback(
    () => navigate('/counselling/seniority'),
    [navigate]
  );
  // Angular: /counselling/seniority-type?path=CounID (the upload-type hub).
  const handleSeniorityType = useCallback(() => {
    navigate('/counselling/seniority-type', {
      state: {
        path: counId,
        subject: localSubject,
        panelType: localPanel,
        block: localBlock,
        manageId: localMange,
      },
    });
  }, [navigate, counId, localSubject, localPanel, localBlock, localMange]);

  // Feed the app-shell breadcrumb (Breadcrumb.tsx renders Home + these):
  //   Update Seniority / <Title> / Excel Uploaded Seniority
  useEffect(() => {
    const items: BreadcrumbItem[] = [
      { label: 'Seniority Update', onClick: handleCounsellingURL },
    ];
    if (counsellingDetails?.Title) {
      items.push({
        label: counsellingDetails.Title,
        onClick: handleSeniorityType,
      });
    }
    items.push({ label: 'Excel Uploaded Seniority' });
    setSubItems(items);
    return () => clearSubItems();
  }, [
    counsellingDetails,
    handleCounsellingURL,
    handleSeniorityType,
    setSubItems,
    clearSubItems,
  ]);

  const hasRows = teacherList.length > 0;

  return (
    <div className="main-body-content">
      {/* Breadcrumb handled by the app shell (see the useBreadcrumb effect). */}
      <div className="Counselling-Secondary card p-4">
        <div className="row">
          <div className="col">
            <label className="fw-bold">Seniority Ordered File</label>
          </div>
        </div>

        <div className="row align-items-center">
          <div className="col-md-3 col-12">
            <input
              ref={fileInputRef}
              type="file"
              name="files"
              className="form-control"
              accept=".xlsx"
              onChange={handleUploadFile}
            />
            <span
              className="cursor-pointer link-hover"
              style={{
                color: 'blue',
                textDecoration: 'underline',
              }}
              onClick={excelTemplate}
            >
              Sample Template
            </span>
          </div>
          <div className="col-md-7 col-12">
            <Button
              type="button"
              icon="pi pi-upload"
              label="Upload"
              onClick={handleUpload}
            />
          </div>
          <div className="col-md-2 col-12 text-end">
            <Button
              type="button"
              label="Export as Excel"
              icon={excelLoading ? 'pi pi-spin pi-spinner' : ''}
              className="p-button-success"
              disabled={!hasRows}
              onClick={gridExcelExport}
            />
          </div>
        </div>
        <br />

        <DataTable
          value={teacherList}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          rowHover
          scrollable
          responsiveLayout="scroll"
          // Task 3 + 11: real empty state, no hardcoded colspan.
          emptyMessage={noDataFound ? 'No Records Found' : ''}
          globalFilter={globalFilter}
          globalFilterFields={globalFilterFields}
          showGridlines
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          header={
            <div className="row">
              <div className="col-md-3 col-12 mb-2">
                <MultiSelect
                  value={selectedCols}
                  options={allColumns}
                  optionLabel="header"
                  placeholder="Choose Columns"
                  selectedItemsLabel="{0} columns selected"
                  className="w-100"
                  onChange={(e) => setSelectedCols(e.value)}
                />
              </div>
              <div className="col-md-6 col-12" />
              <div className="col-md-3 col-12">
                <InputText
                  type="text"
                  placeholder="Search"
                  className="w-100"
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                />
              </div>
            </div>
          }
        >
          {orderedCols.map((col) => (
            <Column
              key={col.field}
              field={col.field}
              header={col.header}
              sortable
              headerStyle={{ whiteSpace: 'nowrap' }}
            />
          ))}
        </DataTable>
      </div>
    </div>
  );
};

export default SeniorityExcelUpload;
